import { useState } from 'react';
import {
  Button,
  Group,
  List,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import type { PtwPermitDetail } from '@isg/shared-types';
import { PTW_ELLE_ICERIK_ETIKETI, ptwKunyeOnayGerekliSchema } from '@isg/validation';
import type { PtwDusecekIcerik } from '@isg/validation';
import { ApiError } from '../api/client';
import { useIzinKunyesi, useIzinSecenekleri } from '../api/queries';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { ANLAM } from '../theme';
import { datetimeLocalDegeri } from '../lib/format';

/**
 * TASLAK KÜNYESİ DÜZENLEYİCİSİ (28.09.2026 — Kapsam §14 K-T1 · K-T2; §16.7.1).
 *
 * Taslak kaydedildikten sonra izin türü · lokasyon · geçerlilik · iş
 * ekipmanları DÜZENLENEMİYORDU (yazma yolu yoktu); künye salt okunur
 * çizildiği için katılımcı da düzenlenemez SANILIYORDU — katılımcı kartı bu
 * yüzden AYNI yüzeye alındı (`IzinKunyesi`).
 *
 * ⚠️ **İŞYERİ BURADA YOKTUR ve olmayacak:** onay zincirini, ekipman havuzunu
 * ve form kayıtlarını bağlar. Yanlış işyeriyle açılan taslak iptal edilir.
 *
 * ⚠️ **HAVUZLAR TALEP FORMUNUNKİYLE AYNI UÇTAN** (`ptw/permits/options`):
 * lokasyon bahçeden, ekipman 1 gün marjından süzülür. İzinde DURAN ama havuzda
 * artık görünmeyen kalem (marjı dolan ekipman · gizlenen tür) seçenek olarak
 * EKLENİR — yoksa kutu onu sessizce düşürür ve kaydetmek onu izinden çıkarırdı.
 *
 * ⚠️ **TÜR ÇIKARMA ONAYI (K-T2):** çıkarılan türün formunda elle girilen içerik
 * varsa sunucu 409 + düşecek içerik listesi döner; pencere listeyi gösterir ve
 * kullanıcı onaylayınca istek `elleIcerikOnayi` ile yeniden gider. Modal burada
 * meşrudur: bir kayıt açmaz, geri alınamayan bir işlemi ONAYLATIR (P1 şerhi).
 */
export function KunyeDuzenleyici({ detay }: { detay: PtwPermitDetail }): React.JSX.Element {
  const secenekler = useIzinSecenekleri(detay.permit.workplaceId);
  const yaz = useIzinKunyesi();

  const kayitli = {
    locationId: detay.permit.locationId,
    baslangic: datetimeLocalDegeri(new Date(detay.permit.gecerlilikBaslangic)),
    bitis: datetimeLocalDegeri(new Date(detay.permit.gecerlilikBitis)),
    turIds: detay.izinTurleri.map((t) => t.refId),
    ekipmanIds: detay.ekipmanlar.map((e) => e.equipmentId),
  };
  const [locationId, setLocationId] = useState<string | null>(kayitli.locationId);
  const [baslangic, setBaslangic] = useState(kayitli.baslangic);
  const [bitis, setBitis] = useState(kayitli.bitis);
  const [turIds, setTurIds] = useState<string[]>(kayitli.turIds);
  const [ekipmanIds, setEkipmanIds] = useState<string[]>(kayitli.ekipmanIds);
  const [dusecek, setDusecek] = useState<PtwDusecekIcerik[] | null>(null);

  const ayniKume = (a: readonly string[], b: readonly string[]): boolean =>
    a.length === b.length && a.every((id) => b.includes(id));
  const degisti =
    locationId !== kayitli.locationId ||
    baslangic !== kayitli.baslangic ||
    bitis !== kayitli.bitis ||
    !ayniKume(turIds, kayitli.turIds) ||
    !ayniKume(ekipmanIds, kayitli.ekipmanIds);
  useKaydedilmemisIcerik(degisti ? 1 : 0, 'kaydedilmemiş künye değişikliği');

  const eksikler = [
    locationId === null ? 'lokasyon' : null,
    baslangic === '' ? 'başlangıç zamanı' : null,
    bitis === '' ? 'bitiş zamanı' : null,
  ].filter((ad): ad is string => ad !== null);
  const sebep = !degisti
    ? null
    : eksikler.length > 0
      ? `Künye için ${eksikler.join(', ')} gerekli.`
      : new Date(bitis) <= new Date(baslangic)
        ? 'Geçerlilik bitişi başlangıçtan sonra olmalı.'
        : null;

  const havuz = secenekler.data;
  // İzinde duran kalem havuzda yoksa seçenek olarak eklenir (yukarıdaki şerh).
  const birlesik = (
    havuzdakiler: readonly { value: string; label: string }[],
    izindekiler: readonly { value: string; label: string }[],
  ): { value: string; label: string }[] => [
    ...havuzdakiler,
    ...izindekiler.filter((k) => !havuzdakiler.some((h) => h.value === k.value)),
  ];
  const lokasyonSecenekleri = birlesik(
    (havuz?.lokasyonlar ?? []).map((l) => ({
      value: l.id,
      // Girinti ağaç derinliğidir — sunucu türetir, kayıtta kolon yok.
      label: `${'— '.repeat(l.seviye)}${l.ad}`,
    })),
    [{ value: detay.permit.locationId, label: detay.permit.locationAd }],
  );
  const turSecenekleri = birlesik(
    (havuz?.izinTurleri ?? []).map((t) => ({ value: t.id, label: t.ad })),
    detay.izinTurleri.map((t) => ({ value: t.refId, label: t.ad })),
  );
  const ekipmanSecenekleri = birlesik(
    (havuz?.ekipmanlar ?? []).map((e) => ({ value: e.equipmentId, label: e.ad })),
    detay.ekipmanlar.map((e) => ({ value: e.equipmentId, label: e.ad })),
  );

  const kaydet = (elleIcerikOnayi: boolean): void => {
    if (locationId === null) return;
    yaz.mutate(
      {
        permitId: detay.permit.id,
        girdi: {
          locationId,
          gecerlilikBaslangic: new Date(baslangic).toISOString(),
          gecerlilikBitis: new Date(bitis).toISOString(),
          izinTuruRefIds: turIds,
          ekipmanIds,
          elleIcerikOnayi,
        },
      },
      {
        onSuccess: () => setDusecek(null),
        onError: (hata) => {
          // K-T2 — 409 gövdesi şemadan geçerse ONAY PENCERESİ açılır; aksi
          // hâlde ret olağan hata satırında görünür.
          if (!(hata instanceof ApiError) || hata.status !== 409) return;
          const govde = ptwKunyeOnayGerekliSchema.safeParse(hata.govde);
          if (govde.success) setDusecek(govde.data.dusecekIcerik);
        },
      },
    );
  };
  const vazgec = (): void => {
    setLocationId(kayitli.locationId);
    setBaslangic(kayitli.baslangic);
    setBitis(kayitli.bitis);
    setTurIds(kayitli.turIds);
    setEkipmanIds(kayitli.ekipmanIds);
    yaz.reset();
  };

  return (
    <Stack gap="sm" data-testid="izin-kunye-duzenle">
      <Group grow align="start">
        <Select
          label="Lokasyon"
          searchable
          allowDeselect={false}
          data={lokasyonSecenekleri}
          value={locationId}
          onChange={setLocationId}
          data-testid="izin-kunye-lokasyon"
        />
        <TextInput
          label="Geçerlilik başlangıcı"
          type="datetime-local"
          value={baslangic}
          onChange={(olay) => setBaslangic(olay.currentTarget.value)}
          data-testid="izin-kunye-baslangic"
        />
        <TextInput
          label="Geçerlilik bitişi"
          type="datetime-local"
          value={bitis}
          onChange={(olay) => setBitis(olay.currentTarget.value)}
          data-testid="izin-kunye-bitis"
        />
      </Group>
      <MultiSelect
        label="İzin türleri"
        searchable
        data={turSecenekleri}
        value={turIds}
        onChange={setTurIds}
        data-testid="izin-kunye-turler"
      />
      <MultiSelect
        label="İş ekipmanları"
        searchable
        data={ekipmanSecenekleri}
        value={ekipmanIds}
        onChange={setEkipmanIds}
        data-testid="izin-kunye-ekipman"
      />
      {yaz.isError && dusecek === null && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-kunye-hata">
          {yaz.error.message}
        </Text>
      )}
      <Group justify="flex-end" align="center">
        {sebep !== null && (
          <Text size="xs" c="dimmed" data-testid="izin-kunye-kaydet-sebep">
            {sebep}
          </Text>
        )}
        {degisti && (
          <Button variant="default" onClick={vazgec} data-testid="izin-kunye-vazgec">
            Vazgeç
          </Button>
        )}
        <Button
          disabled={!degisti || sebep !== null}
          loading={yaz.isPending}
          onClick={() => kaydet(false)}
          data-testid="izin-kunye-kaydet"
        >
          Künyeyi kaydet
        </Button>
      </Group>

      <Modal
        opened={dusecek !== null}
        onClose={() => setDusecek(null)}
        title="Tür çıkarılırsa bu içerik düşer"
        data-testid="izin-kunye-onay"
      >
        <Stack gap="sm">
          {(dusecek ?? []).map((tur) => (
            <Stack key={tur.izinTuruRefId} gap={4}>
              <Text fw={600} size="sm">
                {tur.izinTuruAd}
              </Text>
              <List size="sm" spacing={2} data-testid="izin-kunye-onay-kalemler">
                {tur.kalemler.map((kalem, dizin) => (
                  <List.Item key={`${kalem.sinif}-${kalem.alanEtiketi}-${dizin}`}>
                    {kalem.alanEtiketi} — {PTW_ELLE_ICERIK_ETIKETI[kalem.sinif]}: {kalem.ozet}
                  </List.Item>
                ))}
              </List>
            </Stack>
          ))}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setDusecek(null)}
              data-testid="izin-kunye-onay-vazgec"
            >
              Vazgeç
            </Button>
            <Button
              color={ANLAM.engel}
              loading={yaz.isPending}
              onClick={() => kaydet(true)}
              data-testid="izin-kunye-onay-kaydet"
            >
              Türü çıkar ve içeriği sil
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
